/**
 * Helpers de videos YouTube de listing (UF `user_lot_videos`,
 * contenido de lote `user_entity_videos`, parcela CAD
 * `custom_parcel_videos`). Misma shape y tope.
 * Separado de fotos (listingImageUtils). Sin título. Shorts OK.
 */

export const UF_MAX_VIDEOS = 4;

export const UF_VIDEO_LIMIT_TOAST = {
  title: 'Límite alcanzado',
  message: `Máximo ${UF_MAX_VIDEOS} videos.`,
};

export const UF_VIDEO_INVALID_TOAST = {
  title: 'URL inválida',
  message: 'Pegá un link de YouTube (watch, shorts o youtu.be).',
};

export const UF_VIDEO_DUPLICATE_TOAST = {
  title: 'Video duplicado',
  message: 'Ese video ya está agregado en esta unidad.',
};

/** YouTube video id: 11 chars [A-Za-z0-9_-]. */
export const isValidYoutubeVideoId = (id) => (
  typeof id === 'string' && /^[A-Za-z0-9_-]{11}$/.test(id)
);

/**
 * Extrae el video id de una URL de YouTube (watch, shorts, youtu.be, m.).
 * @param {string} url
 * @returns {string|null}
 */
export const extractYoutubeVideoId = (url) => {
  if (url == null || typeof url !== 'string') return null;
  const trimmed = url.trim();
  if (!trimmed) return null;

  try {
    const parsed = new URL(trimmed.includes('://') ? trimmed : `https://${trimmed}`);
    const host = parsed.hostname.replace(/^www\./i, '').toLowerCase();

    if (host === 'youtu.be') {
      const id = parsed.pathname.split('/').filter(Boolean)[0] || '';
      return isValidYoutubeVideoId(id) ? id : null;
    }

    if (host === 'youtube.com' || host === 'm.youtube.com') {
      if (parsed.pathname.startsWith('/shorts/')) {
        const id = parsed.pathname.split('/').filter(Boolean)[1] || '';
        return isValidYoutubeVideoId(id) ? id : null;
      }
      const fromQuery = parsed.searchParams.get('v');
      if (isValidYoutubeVideoId(fromQuery)) return fromQuery;
      // /embed/ID
      if (parsed.pathname.startsWith('/embed/')) {
        const id = parsed.pathname.split('/').filter(Boolean)[1] || '';
        return isValidYoutubeVideoId(id) ? id : null;
      }
    }
  } catch {
    return null;
  }

  return null;
};

/**
 * Params de embed YouTube (máximo permitido vía URL).
 * Nota: YouTube NO permite quitar logo, menú, "Watch later" ni "Watch on YouTube"
 * del iframe (2023+). `modestbranding` está deprecado y no tiene efecto; se
 * incluye por compatibilidad documentada. `rel=0` solo limita sugerencias al
 * mismo canal (no las elimina).
 * @see https://developers.google.com/youtube/player_parameters
 */
export const YOUTUBE_EMBED_PARAMS = {
  rel: '0',
  modestbranding: '1',
  fs: '1',
  playsinline: '1',
  iv_load_policy: '3',
};

/**
 * Thumbnail estático de YouTube (sin iframe / sin branding de play rojo).
 * @param {string} videoId
 * @param {'maxresdefault'|'sddefault'|'hqdefault'|'mqdefault'|'default'} [quality='hqdefault']
 * @returns {string|null}
 */
export const getYoutubeThumbnailUrl = (videoId, quality = 'hqdefault') => {
  if (!isValidYoutubeVideoId(videoId)) return null;
  const allowed = ['maxresdefault', 'sddefault', 'hqdefault', 'mqdefault', 'default'];
  const safe = allowed.includes(quality) ? quality : 'hqdefault';
  return `https://img.youtube.com/vi/${videoId}/${safe}.jpg`;
};

/**
 * @param {string} videoId
 * @param {Record<string, string>} [extraParams] p.ej. `{ autoplay: '1' }` en lightbox
 * @returns {string|null}
 */
export const getYoutubeEmbedUrl = (videoId, extraParams = {}) => {
  if (!isValidYoutubeVideoId(videoId)) return null;
  const qs = new URLSearchParams({
    ...YOUTUBE_EMBED_PARAMS,
    ...(extraParams && typeof extraParams === 'object' ? extraParams : {}),
  }).toString();
  return `https://www.youtube.com/embed/${videoId}?${qs}`;
};

/**
 * @param {unknown} videos
 * @returns {Array<{ id?: string, youtube_url?: string, youtube_video_id: string, sort_order?: number, created_at?: string }>}
 */
export const normalizeListingVideos = (videos) => {
  const list = Array.isArray(videos) ? videos : [];
  return list
    .map((video) => {
      if (!video || typeof video !== 'object') return null;
      const youtube_video_id = isValidYoutubeVideoId(video.youtube_video_id)
        ? video.youtube_video_id
        : extractYoutubeVideoId(video.youtube_url);
      if (!youtube_video_id) return null;
      return {
        ...video,
        youtube_video_id,
        youtube_url: video.youtube_url
          || `https://www.youtube.com/watch?v=${youtube_video_id}`,
      };
    })
    .filter(Boolean)
    .sort((a, b) => {
      const orderA = Number.isFinite(Number(a.sort_order)) ? Number(a.sort_order) : 0;
      const orderB = Number.isFinite(Number(b.sort_order)) ? Number(b.sort_order) : 0;
      if (orderA !== orderB) return orderA - orderB;
      const ta = a?.created_at ? new Date(a.created_at).getTime() : 0;
      const tb = b?.created_at ? new Date(b.created_at).getTime() : 0;
      if (ta !== tb) return ta - tb;
      return String(a?.id ?? '').localeCompare(String(b?.id ?? ''));
    });
};

export const extractVideosFromApiPayload = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.videos)) return payload.videos;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
};

export const normalizeListingVideosFromPayload = (payload) => (
  normalizeListingVideos(extractVideosFromApiPayload(payload))
);

export const resolveVideoApiMessage = (res) => (
  res?.message || res?.error || res?.detail || ''
);

export const isVideoLimitError = (httpStatus, message = '') => {
  const text = String(message).toLowerCase();
  return httpStatus === 400 && (
    text.includes('límite')
    || text.includes('limite')
    || text.includes('limit')
    || /m[aá]ximo\s*4|de\s*4/.test(text)
  );
};

export const isVideoDuplicateError = (httpStatus, message = '') => {
  const text = String(message).toLowerCase();
  return httpStatus === 400 && (
    text.includes('ya está')
    || text.includes('ya esta')
    || text.includes('duplicate')
    || text.includes('duplicad')
  );
};

export const isInvalidYoutubeUrlError = (httpStatus, message = '') => {
  const text = String(message).toLowerCase();
  return httpStatus === 400 && (
    text.includes('invalid')
    || text.includes('inválid')
    || text.includes('invalid')
    || text.includes('youtube')
    || text.includes('missing')
  ) && !isVideoLimitError(httpStatus, message) && !isVideoDuplicateError(httpStatus, message);
};
